import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config';
import { fetchWithCSRF } from '../../utils/apiUtils';

// Fetch user's conversations
export const fetchConversations = createAsyncThunk(
  'chat/fetchConversations',
  async ({ workspaceId }, { rejectWithValue }) => {
    try {
      const response = await fetchWithCSRF(
        `${config.API_URI}/v1/chat?workspaceId=${workspaceId}`,
        { credentials: 'include' }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || 'Failed to fetch conversations');
      }
      const data = await response.json();
      return data.conversations;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch messages for a conversation
export const fetchMessages = createAsyncThunk(
  'chat/fetchMessages',
  async ({ conversationId, before }, { rejectWithValue }) => {
    try {
      let url = `${config.API_URI}/v1/chat/${conversationId}/messages?limit=30`;
      if (before) url += `&before=${before}`;

      const response = await fetchWithCSRF(url, { credentials: 'include' });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || 'Failed to fetch messages');
      }
      const data = await response.json();
      return { messages: data.messages, hasMore: data.hasMore, before, otherLastRead: data.otherLastRead, conversationId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Send a message
export const sendMessage = createAsyncThunk(
  'chat/sendMessage',
  async ({ conversationId, content, tempId }, { rejectWithValue }) => {
    try {
      const response = await fetchWithCSRF(
        `${config.API_URI}/v1/chat/${conversationId}/messages`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content }),
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || 'Failed to send message');
      }
      const data = await response.json();
      return { message: data.message, tempId };
    } catch (error) {
      return rejectWithValue({ error: error.message, tempId });
    }
  }
);

// Mark conversation as read
export const markAsRead = createAsyncThunk(
  'chat/markAsRead',
  async ({ conversationId }, { rejectWithValue }) => {
    try {
      const response = await fetchWithCSRF(
        `${config.API_URI}/v1/chat/${conversationId}/read`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );
      if (!response.ok) {
        return rejectWithValue('Failed to mark as read');
      }
      return { conversationId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch total unread count
export const fetchUnreadCount = createAsyncThunk(
  'chat/fetchUnreadCount',
  async ({ workspaceId }, { rejectWithValue }) => {
    try {
      const response = await fetchWithCSRF(
        `${config.API_URI}/v1/chat/unread?workspaceId=${workspaceId}`,
        { credentials: 'include' }
      );
      if (!response.ok) {
        return rejectWithValue('Failed to fetch unread count');
      }
      const data = await response.json();
      return data.unreadCount;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Create or find a conversation
export const createConversation = createAsyncThunk(
  'chat/createConversation',
  async ({ participantId, boardId, workspaceId }, { rejectWithValue }) => {
    try {
      const response = await fetchWithCSRF(
        `${config.API_URI}/v1/chat`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ participantId, boardId, workspaceId }),
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || 'Failed to create conversation');
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch board/workspace members for new chat
export const fetchChatMembers = createAsyncThunk(
  'chat/fetchChatMembers',
  async ({ boardId, workspaceId, currentUserId }, { rejectWithValue, getState }) => {
    // Skip if already loaded
    const { chat } = getState();
    if (chat.members.length > 0) return chat.members;

    try {
      let url;
      if (boardId) {
        url = `${config.API_URI}/v1/boards/${boardId}/members`;
      } else if (workspaceId) {
        url = `${config.API_URI}/v1/workspaces/${workspaceId}/members`;
      }
      if (!url) return rejectWithValue('No board or workspace context');

      const response = await fetchWithCSRF(url, { credentials: 'include' });
      if (!response.ok) return rejectWithValue('Failed to fetch members');

      const data = await response.json();
      const raw = data.members || data;
      return (Array.isArray(raw) ? raw : [])
        .map(m => m.user || m)
        .filter(m => m._id && m._id !== currentUserId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const chatSlice = createSlice({
  name: 'chat',
  initialState: {
    conversations: [],
    activeConversation: null,
    messages: [],
    messagesLoading: false,
    conversationsLoading: false,
    unreadCount: 0,
    typingUsers: {},
    hasMoreMessages: true,
    members: [],
    membersLoading: false,
    otherLastRead: {}, // { [conversationId]: timestamp }
  },
  reducers: {
    setActiveConversation: (state, action) => {
      const isSameConversation = state.activeConversation?.shortId === action.payload?.shortId;
      state.activeConversation = action.payload;
      // Only clear messages if switching to a different conversation
      if (!isSameConversation) {
        state.messages = [];
        state.hasMoreMessages = true;
      }
    },
    clearActiveConversation: (state) => {
      state.activeConversation = null;
      state.messages = [];
      state.hasMoreMessages = true;
    },
    addMessageFromSocket: (state, action) => {
      const { conversationId, message } = action.payload;

      // If this message is for the active conversation, add it
      if (state.activeConversation?.shortId === conversationId) {
        const exists = state.messages.some(
          m => m._id === message._id || m.shortId === message.shortId
        );
        if (!exists) {
          state.messages.push(message);
        }
      }

      // Update conversation's lastMessage in the list
      const convIdx = state.conversations.findIndex(
        c => c.shortId === conversationId
      );
      if (convIdx !== -1) {
        state.conversations[convIdx].lastMessage = {
          content: message.content,
          sender: message.sender,
          timestamp: message.createdAt,
        };
        // Move to top
        const [conv] = state.conversations.splice(convIdx, 1);
        state.conversations.unshift(conv);
      }
    },
    replaceOptimisticMessage: (state, action) => {
      const { tempId, message } = action.payload;
      const idx = state.messages.findIndex(m => m._id === tempId);
      if (idx !== -1) {
        state.messages[idx] = message;
      } else {
        // Optimistic already gone, just add if not duplicate
        const exists = state.messages.some(m => m._id === message._id);
        if (!exists) state.messages.push(message);
      }
    },
    updateOtherLastRead: (state, action) => {
      const { conversationId, readAt } = action.payload;
      if (conversationId && readAt) {
        state.otherLastRead[conversationId] = readAt;
      }
    },
    incrementUnreadCount: (state) => {
      state.unreadCount += 1;
    },
    incrementConversationUnread: (state, action) => {
      const { conversationId } = action.payload;
      const conv = state.conversations.find(c => c.shortId === conversationId);
      if (conv) {
        conv.unreadCount = (conv.unreadCount || 0) + 1;
      }
    },
    setTypingUser: (state, action) => {
      const { conversationId, userId } = action.payload;
      if (!state.typingUsers[conversationId]) {
        state.typingUsers[conversationId] = [];
      }
      if (!state.typingUsers[conversationId].includes(userId)) {
        state.typingUsers[conversationId].push(userId);
      }
    },
    clearTypingUser: (state, action) => {
      const { conversationId, userId } = action.payload;
      if (state.typingUsers[conversationId]) {
        state.typingUsers[conversationId] = state.typingUsers[conversationId].filter(
          id => id !== userId
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchConversations — only show loading if no cached data
      .addCase(fetchConversations.pending, (state) => {
        if (state.conversations.length === 0) {
          state.conversationsLoading = true;
        }
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.conversationsLoading = false;
        state.conversations = action.payload;
      })
      .addCase(fetchConversations.rejected, (state) => {
        state.conversationsLoading = false;
      })

      // fetchMessages — only show loading if no cached messages
      .addCase(fetchMessages.pending, (state) => {
        if (state.messages.length === 0) {
          state.messagesLoading = true;
        }
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.messagesLoading = false;
        const { messages, hasMore, before, otherLastRead, conversationId } = action.payload;
        if (before) {
          state.messages = [...messages, ...state.messages];
        } else {
          state.messages = messages;
        }
        state.hasMoreMessages = hasMore;
        if (otherLastRead && conversationId) {
          state.otherLastRead[conversationId] = otherLastRead;
        }
      })
      .addCase(fetchMessages.rejected, (state) => {
        state.messagesLoading = false;
      })

      // sendMessage - optimistic
      .addCase(sendMessage.pending, (state, action) => {
        const { conversationId, content, tempId, senderInfo } = action.meta.arg;
        state.messages.push({
          _id: tempId,
          shortId: tempId,
          content,
          sender: senderInfo || { _id: 'self', name: 'You' },
          createdAt: new Date().toISOString(),
          _isOptimistic: true,
        });
        // Update lastMessage on the conversation
        const conv = state.conversations.find(c => c.shortId === conversationId);
        if (conv) {
          conv.lastMessage = {
            content: content.substring(0, 200),
            timestamp: new Date().toISOString(),
          };
        }
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        const { message, tempId } = action.payload;
        const idx = state.messages.findIndex(m => m._id === tempId);
        if (idx !== -1) {
          state.messages[idx] = message;
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        const tempId = action.meta.arg.tempId;
        state.messages = state.messages.filter(m => m._id !== tempId);
      })

      // markAsRead
      .addCase(markAsRead.fulfilled, (state, action) => {
        const { conversationId } = action.payload;
        const conv = state.conversations.find(c => c.shortId === conversationId);
        if (conv && conv.unreadCount) {
          state.unreadCount = Math.max(0, state.unreadCount - conv.unreadCount);
          conv.unreadCount = 0;
        }
      })

      // fetchUnreadCount
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
      })

      // createConversation
      .addCase(createConversation.fulfilled, (state, action) => {
        const { conversation, isNew } = action.payload;
        if (isNew) {
          state.conversations.unshift(conversation);
        }
        state.activeConversation = conversation;
        state.messages = [];
        state.hasMoreMessages = true;
      })

      // fetchChatMembers
      .addCase(fetchChatMembers.pending, (state) => {
        if (state.members.length === 0) {
          state.membersLoading = true;
        }
      })
      .addCase(fetchChatMembers.fulfilled, (state, action) => {
        state.membersLoading = false;
        state.members = action.payload;
      })
      .addCase(fetchChatMembers.rejected, (state) => {
        state.membersLoading = false;
      });
  },
});

export const {
  setActiveConversation,
  clearActiveConversation,
  addMessageFromSocket,
  updateOtherLastRead,
  incrementUnreadCount,
  incrementConversationUnread,
  setTypingUser,
  clearTypingUser,
} = chatSlice.actions;

export default chatSlice.reducer;
